import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ndfApi } from '../../services/ndf-api';
import type { NoteDeFrais } from '../../types/ndf';
import { BoutonPrincipal, dateFr, Etiquette } from '../../components/ndf/ui/Champ';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { erreurGlobale } from '../../components/ndf/ui/erreurs-champs';
import { useToast } from '../../context/ToastContext';

/**
 * Mes notes de frais.
 *
 * Une note regroupe des dépenses pour les présenter d'un bloc. Tant que le
 * circuit d'approbation n'existe pas (lot suivant), elles restent toutes en
 * brouillon — et l'écran le dit, plutôt que de laisser croire à un envoi qui
 * n'aurait pas fonctionné.
 *
 * Regroupées PAR STATUT : c'est la seule chose qu'on cherche en arrivant ici —
 * ce qui est encore à moi, et ce qui est parti.
 */

export const STATUTS_NOTE: Record<
  string,
  { libelle: string; ton: 'neutre' | 'actif' | 'positif' | 'alerte' | 'attention' }
> = {
  brouillon: { libelle: 'Brouillon', ton: 'neutre' },
  attente_manager: { libelle: 'En attente du manager', ton: 'actif' },
  attente_rh: { libelle: 'En attente de la RH', ton: 'actif' },
  a_rembourser: { libelle: 'À rembourser', ton: 'positif' },
  remboursee: { libelle: 'Remboursée', ton: 'positif' },
  rejetee: { libelle: 'Refusée', ton: 'alerte' },
  cloturee: { libelle: 'Clôturée', ton: 'neutre' },
};

/** L'ordre des sections suit le parcours d'une note, pas l'alphabet. */
const ORDRE = [
  'brouillon',
  'rejetee',
  'attente_manager',
  'attente_rh',
  'a_rembourser',
  'remboursee',
  'cloturee',
];

export function euros(v: number) {
  return v.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

export function NdfMesNotesPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [creation, setCreation] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'notes'],
    queryFn: () => ndfApi.notes.liste(),
    retry: false,
  });
  const associables = useQuery({
    queryKey: ['ndf', 'notes', 'associables'],
    queryFn: () => ndfApi.notes.associables(),
    retry: false,
  });

  const creer = useMutation({
    mutationFn: () => ndfApi.notes.creer(null),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['ndf', 'notes'] });
      // On ouvre la note tout de suite : elle est vide, et la suite évidente
      // est d'y ranger des dépenses.
      navigate(`/notes-de-frais/note/${res.note.id}`);
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'La note n’a pas pu être créée.'),
    onSettled: () => setCreation(false),
  });

  const notes = data?.notes ?? [];
  const parStatut = ORDRE.map((s) => ({
    statut: s,
    notes: notes.filter((n) => n.statut === s),
  })).filter((g) => g.notes.length > 0);
  const libres = associables.data?.depenses.length ?? 0;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="font-titre text-lg font-bold text-nuit">Mes notes de frais</h1>
          <p className="text-sm text-nuit/60">
            {notes.length === 0
              ? 'Aucune note pour l’instant'
              : `${notes.length} note${notes.length > 1 ? 's' : ''}`}
          </p>
        </div>
        <BoutonPrincipal
          type="button"
          disabled={creation || creer.isPending}
          onClick={() => {
            setCreation(true);
            creer.mutate();
          }}
        >
          {creer.isPending ? 'Création…' : 'Nouvelle note de frais'}
        </BoutonPrincipal>
      </header>

      {/* Le rappel qui évite d'oublier des dépenses en route : elles ne partent
          en remboursement que rangées dans une note. */}
      {libres > 0 && (
        <p className="rounded-md border border-ciel/60 border-l-4 border-l-ciel p-3 text-sm text-nuit">
          {libres} dépense{libres > 1 ? 's' : ''} ne {libres > 1 ? 'sont' : 'est'} rattachée
          {libres > 1 ? 's' : ''} à aucune note de frais.{' '}
          <Link to="/notes-de-frais/mes-depenses" className="underline">
            Les voir
          </Link>
        </p>
      )}

      {isLoading && <p className="text-sm text-nuit/60">Chargement…</p>}
      {error && <MessageErreur erreur={error} />}

      {!isLoading && !error && notes.length === 0 && (
        <div className="rounded-lg border border-dashed border-nuit/25 bg-white p-8 text-center">
          <p className="text-sm text-nuit/70">
            Créez une note de frais pour regrouper vos dépenses et les présenter d’un bloc.
          </p>
        </div>
      )}

      {parStatut.map((groupe) => (
        <section key={groupe.statut} className="space-y-2">
          <h2 className="font-titre text-sm font-bold text-nuit">
            {STATUTS_NOTE[groupe.statut]?.libelle ?? groupe.statut}
          </h2>
          <ul className="space-y-2">
            {groupe.notes.map((n) => (
              <li key={n.id}>
                <CarteNote note={n} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function CarteNote({ note }: { note: NoteDeFrais }) {
  const statut = STATUTS_NOTE[note.statut] ?? { libelle: note.statut, ton: 'neutre' as const };

  return (
    <Link
      to={`/notes-de-frais/note/${note.id}`}
      className="block rounded-lg border border-nuit/10 bg-white p-3 active:translate-y-px"
    >
      <span className="flex items-baseline justify-between gap-3">
        <span className="min-w-0">
          {/* Le titre prime sur le numéro : « Salon de Lyon » se reconnaît,
              « NDF_26_0007 » demande un effort. */}
          <span className="block truncate font-titre font-bold text-nuit">
            {note.titre || note.numero}
          </span>
          <span className="block text-sm text-nuit/60">
            {note.titre ? `${note.numero} · ` : ''}
            {note.nombre_depenses} dépense{note.nombre_depenses > 1 ? 's' : ''}
            {note.date_note ? ` · ${dateFr(note.date_note)}` : ''}
            {note.date_remboursement ? ` · remboursée le ${dateFr(note.date_remboursement)}` : ''}
          </span>
        </span>
        <span className="shrink-0 text-right">
          <span className="block font-medium text-nuit">{euros(note.total_ttc)}</span>
          {/* Le montant dû au salarié n'est affiché que s'il diffère du total :
              sinon c'est une répétition qui alourdit la lecture. */}
          {note.total_a_rembourser !== note.total_ttc && (
            <span className="block text-xs text-nuit/60">
              {euros(note.total_a_rembourser)} à rembourser
            </span>
          )}
        </span>
      </span>

      <span className="mt-1 flex flex-wrap items-center gap-1">
        <Etiquette ton={statut.ton}>{statut.libelle}</Etiquette>
        {note.total_facturable > 0 && (
          <Etiquette ton="actif">{euros(note.total_facturable)} refacturables</Etiquette>
        )}
      </span>

      {note.rejet_motif && <span className="mt-1 block text-sm text-corail">{note.rejet_motif}</span>}
    </Link>
  );
}
